import { AssistChip, BodyText, ButtonSelect, Flex, Icon, Select, SelectAsync, Text, Toggle } from '@upsales/components';
import { SelectCoreProps } from '@upsales/components/Select/Select';
import BemClass from '@upsales/components/Utils/bemClass';
import { SelectItem } from '@upsales/components/Utils/selectHelpers';
import ProductAttributes from 'App/babel/attributes/ProductAttributes';
import GenericQuotaResource from 'App/babel/resources/GenericQuota';
import ProductResource from 'App/babel/resources/Product';
import { useFeatureAvailable, useSelector } from 'App/components/hooks';
import { useMetadata, useProductCategories } from 'App/components/hooks/appHooks';
import Product from 'App/resources/Model/Product';
import ProductCategory from 'App/resources/Model/ProductCategory';
import { useTranslation } from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import { MAX_CACHED_PRODUCTS } from 'Helpers/product';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComparisonTypes from 'Resources/ComparisonTypes';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import { getStartAndEndDate } from 'Store/actions/AdminGoalsActions';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { getProducts } from 'Store/selectors/AppSelectors';
import './QuotaProductCategorySelect.scss';

type ProductOrCategory = SelectItem & Product & ProductCategory & { isCategory: boolean };

type QuotaFilters = {
	year: number;
	quotaType: 'productValue' | 'productQuantity';
	// Trigger an update of the quota count when the version changes
	version?: number;
};

type QuotaCounts = {
	product: Record<number, number>;
	category: Record<number, number>;
};

type Props = SelectCoreProps<ProductOrCategory, false> & {
	quotaFilters: QuotaFilters;
};

const QuotaProductCategorySelect = ({
	value,
	onChange,
	className,
	disabled,
	placeholder,
	anchor,
	quotaFilters,
	...selectProps
}: Props) => {
	const classes = new BemClass('QuotaProductCategorySelect', className);
	const selectOptionClasses = new BemClass('SelectOption');
	const { t } = useTranslation();
	const [selectedList, setSelectedList] = useState<'product' | 'category'>('product');
	const [excludeQuotas, setExcludeQuotas] = useState(false);
	const [triggerAsyncSelectRerender, setTriggerAsyncSelectRerender] = useState(false);
	const [quotaCounts, setQuotaCounts] = useState<QuotaCounts>({ product: {}, category: {} });

	const metadata = useMetadata();
	const { productTotal } = useSelector(({ App }) => ({
		productTotal: App.totals.products
	}));
	const cachedProductCategories = useProductCategories(true);
	const cachedProducts = getProducts(true, true, true);

	const useAsyncSelect = productTotal > MAX_CACHED_PRODUCTS;
	const hasProductBundles = useFeatureAvailable(Feature.PRODUCT_BUNDLES);
	const hasArticleNo = metadata?.standardFields.Product.ArticleNo.active;

	const options = useMemo(() => {
		const isProduct = selectedList === 'product';

		let sourceItems: (Product | ProductCategory)[] = isProduct ? cachedProducts : cachedProductCategories;

		if (excludeQuotas) {
			sourceItems = sourceItems.filter(item => !quotaCounts[isProduct ? 'product' : 'category'][item.id]);
		}

		return sourceItems.map(item => ({
			...item,
			title: item.name,
			isCategory: !isProduct
		})) as ProductOrCategory[];
	}, [cachedProducts, cachedProductCategories, selectedList, excludeQuotas, quotaCounts]);

	const buildQuotaFilter = useCallback(
		(listType: typeof selectedList) => {
			const quotaFilterBuilder = new RequestBuilder();
			const quotaDates = getStartAndEndDate(quotaFilters.year);

			quotaFilterBuilder.addFilter(
				Tools.GenericQuota.attr.period,
				comparisonTypes.GreaterThanEquals,
				quotaDates.start.format('YYYY-MM-DD')
			);
			quotaFilterBuilder.addFilter(
				Tools.GenericQuota.attr.period,
				comparisonTypes.LessThanEquals,
				quotaDates.end.toISOString()
			);
			quotaFilterBuilder.addFilter(Tools.GenericQuota.attr.type, comparisonTypes.Equals, quotaFilters.quotaType);

			const filters = quotaFilterBuilder.build();
			filters.type = listType;

			return filters;
		},
		[quotaFilters?.year, quotaFilters?.quotaType]
	);

	const fetchQuotaCounts = useCallback(async () => {
		if (!quotaFilters || Object.keys(quotaFilters).length === 0) {
			setQuotaCounts({ product: {}, category: {} });
			return;
		}
		const productQuotaFilters = buildQuotaFilter('product');
		const categoryQuotaFilters = buildQuotaFilter('category');

		try {
			const handleCountsResponse = (counts: {
				data: { productId?: number; productCategoryId?: number; count: number }[];
			}) => {
				const countsMap: Record<number, number> = {};
				for (const item of counts.data) {
					const id = item.productId ?? item.productCategoryId;
					if (id) {
						countsMap[id] = item.count;
					}
				}
				return countsMap;
			};

			const productCountsPromise =
				GenericQuotaResource.getProductQuotaCounts(productQuotaFilters).then(handleCountsResponse);
			const categoryCountsPromise =
				GenericQuotaResource.getProductQuotaCounts(categoryQuotaFilters).then(handleCountsResponse);
			const [productQuotaCounts, categoryQuotaCounts] = await Promise.all([
				productCountsPromise,
				categoryCountsPromise
			]);

			setQuotaCounts({ product: productQuotaCounts, category: categoryQuotaCounts });
		} catch (error) {
			logError(error, 'Failed to fetch quota counts');
			setQuotaCounts({ product: {}, category: {} });
		}
	}, [buildQuotaFilter]);

	useEffect(() => {
		fetchQuotaCounts();
	}, [fetchQuotaCounts, quotaFilters?.quotaType, quotaFilters?.year, quotaFilters?.version]);

	useEffect(() => {
		if (useAsyncSelect) {
			// We need some way to trigger a rerender of items when the excludeQuotas state changes
			// It can be done by removing the fetcher function from the SelectAsync and adding it again
			setTriggerAsyncSelectRerender(true);
			setTimeout(() => {
				setTriggerAsyncSelectRerender(false);
			});
		}
		// excludeQuotas, when the toggle is changed,
		// quotaCounts, when the quota counts are updated after a change in the quota filters
	}, [excludeQuotas, quotaCounts, useAsyncSelect]);

	const fetcherProducts = async (searchString: string): Promise<ProductOrCategory[]> => {
		const filter = new RequestBuilder();
		filter.addFilter(ProductAttributes.name, ComparisonTypes.Search, searchString);
		filter.addSort(ProductAttributes.name, true);
		filter.limit = 500;

		if (excludeQuotas) {
			filter.addFilter({ field: 'hasQuota' }, comparisonTypes.Equals, false);
			const quotaFilter = buildQuotaFilter('product');
			filter.addExtraParam('quotaFilter', quotaFilter);
		}

		const productsData = await ProductResource.find(filter.build());

		return productsData.data.map((item: Product) => ({
			...item,
			title: item.name,
			isCategory: false
		})) as ProductOrCategory[];
	};

	const fetcherProductCategories = async (searchString: string): Promise<ProductOrCategory[]> => {
		return searchString
			? options.filter(option => option.title.toLowerCase().includes(searchString.toLowerCase()))
			: options;
	};

	const renderCustomHeader = () => (
		<Flex spacing="m2" gap={'u2'} direction="column" alignItems="flex-end">
			<ButtonSelect
				className={classes.elem('selectHeader').b()}
				size="sm"
				value={selectedList}
				onChange={setSelectedList}
				options={[
					{ value: 'product', title: t('default.products') },
					{ value: 'category', title: t('default.productCategories') }
				]}
			/>
			<Flex gap={'u2'}>
				<Toggle checked={excludeQuotas} onChange={() => setExcludeQuotas(!excludeQuotas)} />
				<BodyText size="sm">{t('admin.quota.excludeQuotas')}</BodyText>
			</Flex>
		</Flex>
	);

	const renderItem = (option: ProductOrCategory) => {
		const { title, articleNo, tiers, bundle, id, isCategory } = option;
		const hasQuota = quotaCounts[isCategory ? 'category' : 'product'][id] > 0;

		return (
			<Flex className={selectOptionClasses.elem('inner').b()} direction="column">
				<Flex justifyContent="space-between">
					<BodyText size="md" ellipsis>
						{title + ' '}
						{tiers?.length ? <Icon name="tiers" /> : null}
						{hasProductBundles && bundle?.length ? <Icon name="bundle" /> : null}
					</BodyText>
					{hasQuota ? <AssistChip title={t('admin.quota.hasQuota')} /> : null}
				</Flex>
				{hasArticleNo && articleNo ? (
					<BodyText size="sm" color="neutral-550">
						{articleNo}
					</BodyText>
				) : null}
			</Flex>
		);
	};

	const renderSelectedItem = (option: ProductOrCategory | null) => {
		if (!option || (Array.isArray(option) && option.length === 0)) {
			return (
				<Text italic color="grey-10">
					{placeholder || t('default.select')}
				</Text>
			);
		}

		const title = option.isCategory
			? option.title
			: `${option.title}${hasArticleNo && option.articleNo ? ` (${option.articleNo})` : ''}`;
		return <BodyText ellipsis>{title}</BodyText>;
	};

	const generalProps: SelectCoreProps<ProductOrCategory, false> = {
		anchor,
		disabled,
		renderItem,
		renderCustomHeader,
		renderSelectedItem,
		onChange,
		optionHeaderType: 'disabled',
		dropdownClassName: classes.elem('select-width').b(),
		language: {
			typeToSearch: selectedList === 'product' ? t('product.searchProducts') : t('product.searchCategories')
		},
		value,
		virtualized: true,
		ellipsisTooltip: true,
		...selectProps
	};

	return (
		<div className={classes.b()}>
			{useAsyncSelect ? (
				<SelectAsync
					{...generalProps}
					fetchOnOpen
					fetcher={
						triggerAsyncSelectRerender
							? () => Promise.resolve([])
							: selectedList === 'product'
							? fetcherProducts
							: fetcherProductCategories
					}
				/>
			) : (
				<Select {...generalProps} options={options} />
			)}
		</div>
	);
};

export default QuotaProductCategorySelect;
